import { useEffect, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { format } from "date-fns";
import { ChevronDown, Phone, Mail, CalendarIcon } from "lucide-react";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { SERVICES } from "@/constants/services";

const TOUR_TITLE = "Full Day Tour — Sydney to the Blue Mountains";

const TIME_SLOTS = [];
for (let h = 5; h <= 22; h++) {
  TIME_SLOTS.push(`${String(h).padStart(2, "0")}:00`);
  if (h < 22) TIME_SLOTS.push(`${String(h).padStart(2, "0")}:30`);
}

const toDate = (s) => {
  const [y, m, d] = s.split("-").map(Number);
  return new Date(y, m - 1, d);
};

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const field =
  "w-full bg-white/[0.04] border border-white/10 px-4 py-3.5 text-sm text-[#fcfcfc] placeholder:text-[#555] focus:outline-none focus:border-[#d4af37] focus:ring-1 focus:ring-[#d4af37] transition-colors duration-300";
const label = "block text-[10px] tracking-[0.3em] uppercase text-[#888] mb-2.5";

const blankForm = {
  name: "",
  email: "",
  phone: "",
  pickup: "",
  dropoff: "",
  date: "",
  time: "",
  passengers: "1",
  suitcases: "0",
  large_bags: "0",
  return_transfer: "No",
  flight_number: "",
  venue: "",
  notes: "",
};

export default function BookingForm({ service, onServiceChange }) {
  const [form, setForm] = useState(blankForm);
  const [submitting, setSubmitting] = useState(false);
  const [availability, setAvailability] = useState([]);
  const [calendarOpen, setCalendarOpen] = useState(false);

  const isTour = service === "full-day-tour";

  useEffect(() => {
    axios
      .get(`${API}/availability`)
      .then((res) => setAvailability(res.data.booked || []))
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (isTour && parseInt(form.passengers, 10) < 4) {
      setForm((f) => ({ ...f, passengers: "4" }));
    }
  }, [service]);

  const dateParts = (form.date || "").split("-").map(Number);
  const dateObj =
    form.date && dateParts.length === 3
      ? new Date(dateParts[0], dateParts[1] - 1, dateParts[2])
      : null;
  const dayInvalid = isTour && dateObj && ![2, 4].includes(dateObj.getDay());

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const tourDates = availability
    .filter((b) => b.service_type === TOUR_TITLE)
    .map((b) => b.date);
  const datesWithBookings = [...new Set(availability.map((b) => b.date))];
  const bookedTimes = form.date
    ? availability.filter((b) => b.date === form.date).map((b) => b.time)
    : [];
  const disabledDays = [
    { before: today },
    ...tourDates.map(toDate),
    ...(isTour
      ? [{ dayOfWeek: [0, 1, 3, 5, 6] }, ...datesWithBookings.map(toDate)]
      : []),
  ];

  const update = (key) => (e) =>
    setForm((f) => ({ ...f, [key]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (dayInvalid) return;
    setSubmitting(true);
    const serviceTitle =
      SERVICES.find((s) => s.slug === service)?.title || service;
    try {
      await axios.post(`${API}/enquiries`, {
        ...form,
        service_type: serviceTitle,
        passengers: parseInt(form.passengers, 10),
        suitcases: parseInt(form.suitcases, 10),
        large_bags: parseInt(form.large_bags, 10),
      });
      toast.success("Booking request received.", {
        description:
          "A confirmation email is on its way — Daniel will confirm your booking personally.",
      });
      setAvailability((prev) => [
        ...prev,
        { date: form.date, time: form.time, service_type: serviceTitle },
      ]);
      setForm(blankForm);
    } catch (err) {
      toast.error("Booking could not be completed.", {
        description:
          err.response?.data?.detail ||
          "Please try again, or call 0452 512 770 directly.",
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      id="booking"
      className="py-32 border-t border-white/10"
      data-testid="booking-section"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-12 gap-16">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.9 }}
          className="lg:col-span-5"
        >
          <p className="text-xs tracking-[0.35em] uppercase text-[#d4af37] mb-6">
            Book
          </p>
          <h2 className="font-serif-lux text-3xl sm:text-4xl lg:text-5xl font-light tracking-tight mb-8">
            Reserve your journey.
          </h2>
          <p className="text-base leading-relaxed tracking-wide text-[#888] max-w-md mb-12">
            Choose your service, tell us where and when. Daniel confirms every
            booking personally — usually within the hour, always within the
            day.
          </p>
          <div className="space-y-6">
            <a
              href="tel:+61452512770"
              className="flex items-center gap-4 group"
              data-testid="booking-contact-phone"
            >
              <span className="w-10 h-10 border border-[#d4af37]/40 flex items-center justify-center text-[#d4af37]">
                <Phone size={15} strokeWidth={1.5} />
              </span>
              <div>
                <p className="text-[10px] tracking-[0.3em] uppercase text-[#888]">
                  Call or Text
                </p>
                <p className="text-sm text-[#fcfcfc] mt-1 group-hover:text-[#d4af37] transition-colors duration-300">
                  0452 512 770
                </p>
              </div>
            </a>
            <a
              href="mailto:lucasprivatetransfers@gmail.com"
              className="flex items-center gap-4 group"
              data-testid="booking-contact-email"
            >
              <span className="w-10 h-10 border border-[#d4af37]/40 flex items-center justify-center text-[#d4af37]">
                <Mail size={15} strokeWidth={1.5} />
              </span>
              <div>
                <p className="text-[10px] tracking-[0.3em] uppercase text-[#888]">
                  Email
                </p>
                <p className="text-sm text-[#fcfcfc] mt-1 group-hover:text-[#d4af37] transition-colors duration-300">
                  lucasprivatetransfers@gmail.com
                </p>
              </div>
            </a>
          </div>
        </motion.div>

        <motion.form
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.9, delay: 0.15 }}
          onSubmit={handleSubmit}
          className="lg:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-6"
          data-testid="booking-form"
        >
          <div className="relative sm:col-span-2">
            <label className={label} htmlFor="booking-service">Service</label>
            <select
              id="booking-service"
              data-testid="booking-service-select"
              value={service}
              onChange={(e) => onServiceChange(e.target.value)}
              className={`${field} appearance-none pr-10 border-[#d4af37]/40`}
            >
              {SERVICES.map((s) => (
                <option key={s.slug} value={s.slug} className="bg-[#0a0a0a]">
                  {s.title}
                </option>
              ))}
            </select>
            <ChevronDown size={14} className="absolute right-4 bottom-4 text-[#888] pointer-events-none" />
          </div>
          {service === "full-day-tour" && (
            <div
              className="sm:col-span-2 border border-[#d4af37]/30 bg-[#d4af37]/5 px-5 py-4"
              data-testid="booking-tour-price-note"
            >
              <p className="text-[11px] tracking-[0.25em] uppercase text-[#d4af37]">
                Full Day Tour — Tuesdays & Thursdays · 4–7 guests · $550 per
                person · GST inclusive
              </p>
              <p className="mt-2 text-sm text-[#aaa]">
                Estimated total for {form.passengers}{" "}
                {parseInt(form.passengers, 10) === 1 ? "guest" : "guests"}: $
                {550 * parseInt(form.passengers, 10)} incl. GST — confirmed
                personally when Daniel calls.
              </p>
            </div>
          )}
          <div>
            <label className={label} htmlFor="booking-name">Full Name</label>
            <input id="booking-name" data-testid="booking-name-input" required value={form.name} onChange={update("name")} placeholder="Your name" className={field} />
          </div>
          <div>
            <label className={label} htmlFor="booking-email">Email</label>
            <input id="booking-email" data-testid="booking-email-input" required type="email" value={form.email} onChange={update("email")} placeholder="you@example.com" className={field} />
          </div>
          <div>
            <label className={label} htmlFor="booking-phone">Phone</label>
            <input id="booking-phone" data-testid="booking-phone-input" required value={form.phone} onChange={update("phone")} placeholder="04 ..." className={field} />
          </div>
          <div className="relative">
            <label className={label} htmlFor="booking-passengers">Passengers</label>
            <select id="booking-passengers" data-testid="booking-passengers-select" value={form.passengers} onChange={update("passengers")} className={`${field} appearance-none pr-10`}>
              {(isTour ? [4, 5, 6, 7] : [1, 2, 3, 4, 5, 6, 7, 8, 9, 10]).map((n) => (
                <option key={n} value={n} className="bg-[#0a0a0a]">{n}</option>
              ))}
            </select>
            <ChevronDown size={14} className="absolute right-4 bottom-4 text-[#888] pointer-events-none" />
          </div>
          <div className="relative">
            <label className={label} htmlFor="booking-suitcases">Suitcases</label>
            <select id="booking-suitcases" data-testid="booking-suitcases-select" value={form.suitcases} onChange={update("suitcases")} className={`${field} appearance-none pr-10`}>
              {[0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((n) => (
                <option key={n} value={n} className="bg-[#0a0a0a]">{n}</option>
              ))}
            </select>
            <ChevronDown size={14} className="absolute right-4 bottom-4 text-[#888] pointer-events-none" />
          </div>
          <div className="relative">
            <label className={label} htmlFor="booking-large-bags">Other Large / Hiking Bags</label>
            <select id="booking-large-bags" data-testid="booking-large-bags-select" value={form.large_bags} onChange={update("large_bags")} className={`${field} appearance-none pr-10`}>
              {[0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((n) => (
                <option key={n} value={n} className="bg-[#0a0a0a]">{n}</option>
              ))}
            </select>
            <ChevronDown size={14} className="absolute right-4 bottom-4 text-[#888] pointer-events-none" />
          </div>
          <div className="relative">
            <label className={label} htmlFor="booking-return">Return Transfer Required</label>
            <select id="booking-return" data-testid="booking-return-select" value={form.return_transfer} onChange={update("return_transfer")} className={`${field} appearance-none pr-10`}>
              <option value="No" className="bg-[#0a0a0a]">No</option>
              <option value="Yes" className="bg-[#0a0a0a]">Yes</option>
            </select>
            <ChevronDown size={14} className="absolute right-4 bottom-4 text-[#888] pointer-events-none" />
          </div>
          <div>
            <label className={label} htmlFor="booking-pickup">Pickup</label>
            <input id="booking-pickup" data-testid="booking-pickup-input" required value={form.pickup} onChange={update("pickup")} placeholder="e.g. Katoomba Station" className={field} />
          </div>
          <div>
            <label className={label} htmlFor="booking-dropoff">Drop-off</label>
            <input id="booking-dropoff" data-testid="booking-dropoff-input" required value={form.dropoff} onChange={update("dropoff")} placeholder="e.g. Sydney Airport" className={field} />
          </div>
          <div>
            <label className={label} id="booking-date-label">
              Date{isTour ? " — Tuesdays & Thursdays" : ""}
            </label>
            <Popover open={calendarOpen} onOpenChange={setCalendarOpen}>
              <PopoverTrigger asChild>
                <button
                  type="button"
                  aria-labelledby="booking-date-label"
                  data-testid="booking-date-button"
                  className={`${field} flex items-center justify-between text-left ${form.date ? "" : "text-[#555]"}`}
                >
                  <span>{dateObj ? format(dateObj, "EEEE, d MMMM yyyy") : "Select a date"}</span>
                  <CalendarIcon size={14} className="text-[#888]" />
                </button>
              </PopoverTrigger>
              <PopoverContent
                className="w-auto p-0 bg-[#0a0a0a] border border-white/10 text-[#fcfcfc]"
                align="start"
                data-testid="booking-calendar"
              >
                <Calendar
                  mode="single"
                  selected={dateObj || undefined}
                  onSelect={(d) => {
                    setForm((f) => ({
                      ...f,
                      date: d ? format(d, "yyyy-MM-dd") : "",
                      time: "",
                    }));
                    setCalendarOpen(false);
                  }}
                  disabled={disabledDays}
                  initialFocus
                />
              </PopoverContent>
            </Popover>
            {form.date && tourDates.includes(form.date) && (
              <p className="mt-2 text-xs tracking-wide text-red-400" data-testid="booking-date-booked-error">
                This date is booked out.
              </p>
            )}
            {dayInvalid && (
              <p className="mt-2 text-xs tracking-wide text-red-400" data-testid="booking-date-error">
                The Full Day Tour runs on Tuesdays and Thursdays only.
              </p>
            )}
          </div>
          <div className="relative">
            <label className={label} htmlFor="booking-time">Pickup Time</label>
            <select id="booking-time" data-testid="booking-time-select" required value={form.time} onChange={update("time")} className={`${field} appearance-none pr-10`}>
              <option value="" disabled className="bg-[#0a0a0a]">Select a time</option>
              {TIME_SLOTS.map((t) => (
                <option key={t} value={t} disabled={bookedTimes.includes(t)} className="bg-[#0a0a0a]">
                  {t}{bookedTimes.includes(t) ? " — booked" : ""}
                </option>
              ))}
            </select>
            <ChevronDown size={14} className="absolute right-4 bottom-4 text-[#888] pointer-events-none" />
          </div>
          {service === "airport-transfers" && (
            <div className="sm:col-span-2">
              <label className={label} htmlFor="booking-flight">Flight Number</label>
              <input id="booking-flight" data-testid="booking-flight-input" value={form.flight_number} onChange={update("flight_number")} placeholder="e.g. QF423 — we track it live" className={field} />
            </div>
          )}
          {service === "wedding-transfers" && (
            <div className="sm:col-span-2">
              <label className={label} htmlFor="booking-venue">Wedding Venue</label>
              <input id="booking-venue" data-testid="booking-venue-input" value={form.venue} onChange={update("venue")} placeholder="e.g. Dryridge Estate, Megalong Valley" className={field} />
            </div>
          )}
          <div className="sm:col-span-2">
            <label className={label} htmlFor="booking-notes">Notes</label>
            <textarea id="booking-notes" data-testid="booking-notes-input" rows={4} value={form.notes} onChange={update("notes")} placeholder="Luggage, child seats, extra stops, tour ideas..." className={`${field} resize-none`} />
          </div>
          <button
            type="submit"
            disabled={submitting || dayInvalid}
            data-testid="booking-submit-button"
            className="sm:col-span-2 bg-[#d4af37] text-[#050505] px-8 py-5 text-[11px] tracking-[0.3em] uppercase font-medium hover:bg-[#f3c746] transition-colors duration-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {submitting ? "Sending..." : "Request This Booking"}
          </button>
        </motion.form>
      </div>
    </section>
  );
}
